/**
 * @file ComingUpSection.tsx
 * @brief Top of Home (calm Home, DES 19-10-2026): the next meetings from the
 *        connected calendar, each with "Start notes", else one
 *        "Connect your calendar" card.
 *
 * @details
 * Mac app: the Mac's calendar (useDesktopCalendar, DESKTOP calendar.js). It
 * carries Apple Calendar and every Google or Microsoft account added to the
 * Mac (System Settings → Internet Accounts), so one Connect covers all three.
 * The per-user connect rule, "Not now" and "Choose calendars" are the
 * Boardrooms panel's own (CalendarMeetingsPanel), shared through the same
 * hook and storage keys: dismissing here dismisses there.
 *
 * Browser: there is no Google / Microsoft calendar sign-in on the web yet
 * (no backend OAuth). The card says where it works today (the Mac app and
 * the phone) instead of offering buttons that go nowhere; "Not now" hides it
 * for this user on this browser.
 *
 * "Start notes" opens the recorder in Meeting Notes mode with the title and
 * the attendees filled in, after the recording notice (GDPR Art 13) in a
 * confirm, the same as a scheduled meeting's start (`scheduled_confirm`).
 *
 * Related: ./comingUp.ts, ../notes/useStartMeetingNotes.ts,
 *          ../employee-boardroom/CalendarMeetingsPanel.tsx
 *
 * @author DES
 * @date 20-10-2026
 */

import { useState, type ReactNode } from "react";
import { CalendarDays, CalendarOff, CalendarPlus, Mic, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { cn } from "@/shared/theme/cn";
import {
  CalendarMenu,
  OpenCalendarSettingsButton,
  whenLabel,
} from "@/features/employee-boardroom/CalendarMeetingsPanel";
import type { CalendarMeeting } from "@/features/employee-boardroom/calendarMeetings";
import { connectHint, type DesktopCalendarState } from "@/features/employee-boardroom/useDesktopCalendar";
import { useStartMeetingNotes } from "@/features/notes/useStartMeetingNotes";
import { comingUpMeetings, peopleLabel } from "./comingUp";

const BROWSER_CARD_KEY = (uid: string) => `vz.home.calendarCardDismissed.${uid}`;

function readDismissed(uid: string): boolean {
  try {
    return window.localStorage.getItem(BROWSER_CARD_KEY(uid)) === "1";
  } catch {
    return false;
  }
}

const cardClass = "mx-5 flex items-center gap-3 rounded-2xl bg-black/[0.04] px-4 py-3.5 dark:bg-white/[0.06]";

function Heading({ right }: { right?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3 px-5">
      <h2 className="text-lg font-semibold tracking-[-0.5px] text-app-text">Coming up</h2>
      {right}
    </div>
  );
}

function NotNow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Not now"
      className="shrink-0 rounded-full p-1 text-app-text-secondary transition-colors hover:text-app-text"
    >
      <X className="h-[18px] w-[18px]" />
    </button>
  );
}

export function ComingUpSection({
  calendar,
  userId,
  boardroomBase,
  canRecord,
}: {
  calendar: DesktopCalendarState;
  userId: string;
  /** Where the recorder lives for this shell (/app, /company, /admin). */
  boardroomBase: string;
  /** Boardroom entitlement: without it there is nothing to start. */
  canRecord: boolean;
}) {
  const notes = useStartMeetingNotes(boardroomBase);
  const [confirm, setConfirm] = useState<CalendarMeeting | null>(null);
  const [browserDismissed, setBrowserDismissed] = useState(() => readDismissed(userId));

  if (!canRecord) return null;

  // ---- Browser (or a Mac app too old for the calendar) ----
  if (!calendar.enabled) {
    if (browserDismissed) return null;
    return (
      <section className="mb-6 mt-5">
        <Heading />
        <div className={cardClass}>
          <CalendarPlus className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
          <span className="min-w-0 flex-1">
            <span className="block text-[17px] font-bold text-app-text">Connect your calendar</span>
            <span className="block text-sm text-app-text-secondary">
              Google, Microsoft and Apple calendars connect in the VERBLIZR Mac app and the phone
              app, then your next meetings show here with Start notes. In the browser: coming soon.
            </span>
          </span>
          <NotNow
            onClick={() => {
              setBrowserDismissed(true);
              try {
                window.localStorage.setItem(BROWSER_CARD_KEY(userId), "1");
              } catch {
                // Storage blocked: hidden until the page reloads.
              }
            }}
          />
        </div>
      </section>
    );
  }

  const { status, loaded, connectDismissed } = calendar;
  if (status === "loading" || status === "unsupported") return null;
  // "Not now" on Home or on Boardrooms: the Boardrooms heading keeps Connect.
  if (connectDismissed && status !== "authorized") return null;

  if (status === "notDetermined") {
    return (
      <section className="mb-6 mt-5">
        <Heading />
        <div className={cardClass}>
          <button
            type="button"
            onClick={() => void calendar.connect()}
            disabled={calendar.connectPhase === "waiting"}
            aria-busy={calendar.connectPhase === "waiting"}
            className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80 disabled:cursor-wait disabled:hover:opacity-100"
          >
            <CalendarPlus className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
            <span className="min-w-0 flex-1">
              <span className="block text-[17px] font-bold text-app-text">Connect your calendar</span>
              <span className="block text-sm text-app-text-secondary" aria-live="polite">
                {connectHint(
                  calendar,
                  "Apple Calendar, plus the Google and Microsoft accounts on this Mac. Your next meetings show here, ready to take notes.",
                )}
              </span>
            </span>
          </button>
          <NotNow onClick={calendar.dismissConnect} />
        </div>
      </section>
    );
  }

  if (status !== "authorized") {
    return (
      <section className="mb-6 mt-5">
        <Heading />
        <div className={cardClass}>
          <CalendarOff className="h-6 w-6 shrink-0 text-app-text-secondary" strokeWidth={1.5} />
          <span className="min-w-0 flex-1">
            <span className="block text-[17px] font-bold text-app-text">Calendar access is off</span>
            <span className="block text-sm text-app-text-secondary">
              Turn it on in System Settings → Privacy &amp; Security → Calendars
            </span>
          </span>
          <OpenCalendarSettingsButton calendar={calendar} />
          <NotNow onClick={calendar.dismissConnect} />
        </div>
      </section>
    );
  }

  if (!loaded) return null;
  const rows = comingUpMeetings(calendar.meetings);

  return (
    <section className="mb-6 mt-5">
      <Heading right={<CalendarMenu calendar={calendar} />} />
      {rows.length === 0 ? (
        <p className="px-5 text-[15px] text-app-text-secondary">
          No meetings today or tomorrow.
        </p>
      ) : (
        <ul className="px-5">
          {rows.map((m) => {
            const live = m.startMs <= Date.now();
            const people = peopleLabel(m.attendees);
            return (
              <li key={m.id} className="flex items-center gap-3 py-2">
                <CalendarDays
                  className={cn("h-6 w-6 shrink-0", live ? "text-brand" : "text-app-text-secondary")}
                  strokeWidth={1.5}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] font-bold text-app-text">{m.title}</span>
                  <span className={cn("block truncate text-sm", live ? "text-brand" : "text-app-text-secondary")}>
                    {whenLabel(m)}
                    {people ? `  ·  ${people}` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={notes.starting}
                  onClick={() => setConfirm(m)}
                  aria-label={`Start notes for ${m.title}`}
                  className="inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-full bg-black/[0.04] px-3.5 text-sm font-semibold text-app-text transition-colors hover:bg-black/[0.08] disabled:opacity-50 dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
                >
                  <Mic className="h-4 w-4 text-brand" strokeWidth={2} />
                  Start notes
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* GDPR Art 13: no Setup slide here, so the notice is confirmed first,
          as a scheduled meeting's start (06-10-2026). */}
      <AlertDialog open={confirm != null} onOpenChange={(open) => (open ? null : setConfirm(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start notes for {confirm?.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              By starting, you confirm everyone in this meeting knows it is recorded and
              transcribed. Meeting Notes are private to you.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-0 bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const m = confirm;
                setConfirm(null);
                if (m) {
                  void notes.start({
                    title: m.title,
                    attendees: m.attendees,
                    noticeMethod: "scheduled_confirm",
                  });
                }
              }}
            >
              Start notes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
